/**
 * 캠핑 허브의 **인원 축**(10-05) — 텐트 세트가 몇 인용인가. 순수 모듈.
 *
 * 값은 상품 태그에서 온다: hinata 텐트 세트는 `"1人用"`·`"2人用"`·`"3人用"`·`"4人用"`
 * 태그를 하나씩 달고 있다(prod 19개 전부 확인, 10-05). 태그가 없으면 이름의 `N人用`,
 * 그것도 없으면 태그·이름의 `ソロ`(솔로)를 1인으로 본다 — `N人用` 이 있으면 그쪽이 먼저다
 * ("ソロ用フェスセット" 처럼 둘 다 있는 상품은 태그 1人用 이 정본).
 *
 * 축 키는 숫자(1~4)라 라벨은 i18n 메시지(`search.hub.camping.partyN`, ICU 복수형)가 맡는다.
 * 5인 이상 세트가 들어오면 `PARTY_SIZES` 만 늘리면 된다(URL·알약·파싱이 함께 따라온다).
 */

export const PARTY_SIZES = [1, 2, 3, 4] as const;
export type PartySize = (typeof PARTY_SIZES)[number];

const PARTY_SET = new Set<number>(PARTY_SIZES);

/** `N人用`·`N名用`(전각 숫자 포함) — 글자 사이 공백 허용. */
const PARTY_PATTERN = /([0-9０-９])\s*[人名]\s*用/;
const SOLO_PATTERN = /ソロ/;

/** 전각 숫자 → 반각. */
function toHalfWidthDigit(ch: string): string {
  const code = ch.charCodeAt(0);
  return code >= 0xff10 && code <= 0xff19 ? String(code - 0xff10) : ch;
}

/** 알려진 인원 키만 — 나머지(0·5 이상·문자)는 null. */
export function parsePartySize(value: unknown): PartySize | null {
  const n =
    typeof value === "number"
      ? value
      : typeof value === "string" && value.trim() !== ""
        ? Number(toHalfWidthDigit(value.trim()))
        : NaN;
  return Number.isInteger(n) && PARTY_SET.has(n) ? (n as PartySize) : null;
}

/** 문자열 하나에서 `N人用` 을 찾는다. */
function partyFromText(text: string | null | undefined): PartySize | null {
  if (!text) return null;
  const match = PARTY_PATTERN.exec(text);
  return match ? parsePartySize(toHalfWidthDigit(match[1])) : null;
}

/**
 * 상품의 인원 — 태그 `N人用` → 이름 `N人用` → 태그·이름 `ソロ`(=1) 순. 없으면 null.
 * 이름은 여러 개(원문·현지화 제목)를 받아 앞에서부터 본다.
 */
export function resolvePartySize(
  tags: readonly (string | null | undefined)[] | null | undefined,
  ...names: Array<string | null | undefined>
): PartySize | null {
  for (const tag of tags ?? []) {
    const size = partyFromText(tag);
    if (size) return size;
  }
  for (const name of names) {
    const size = partyFromText(name);
    if (size) return size;
  }
  const solo =
    (tags ?? []).some((tag) => !!tag && SOLO_PATTERN.test(tag)) ||
    names.some((name) => !!name && SOLO_PATTERN.test(name));
  return solo ? 1 : null;
}

/** 쉼표 조각들 → 알려진 키만, 중복 제거·입력 순서 유지. */
export function parsePartySizes(parts: Iterable<string>): PartySize[] {
  const out: PartySize[] = [];
  for (const part of parts) {
    const size = parsePartySize(part);
    if (size && !out.includes(size)) out.push(size);
  }
  return out;
}

/** 데이터셋에 실제로 있는 인원만, 오름차순 — 알약을 그릴지·타일에 무엇을 넣을지의 근거. */
export function collectPartySizes(
  sizes: Iterable<PartySize | null | undefined>,
): PartySize[] {
  const present = new Set<number>();
  for (const size of sizes) if (size) present.add(size);
  return PARTY_SIZES.filter((size) => present.has(size));
}

/** 선택(축 안 OR)에 들어 있는가 — 선택이 비면 전부 통과, 인원을 모르는 상품은 빠진다. */
export function matchesParty(
  size: PartySize | null | undefined,
  selected: readonly PartySize[],
): boolean {
  if (selected.length === 0) return true;
  return !!size && selected.includes(size);
}
